import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { toast } from "react-toastify";
import { Context } from "../../../../../store/store";
import { handlePdfFileDownload } from "./utils";
import Button from "../../components/button";
import Loader from "../../../../../components/Loader";
import { StoreContextType } from "@/store/types";
import { BottomBar } from "../../components/bottom-bar";
import { Module } from "../../../../../constants";
import { useUrlState } from "../../hooks";
import { INTRO_SUB_URL } from "../../../constants";

import "./styles.scss";

export function ModuleEnd() {
  const [{ publicTest }] = useContext<StoreContextType>(Context as any);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { module: currentModule, navigateToParticipationPage } = useUrlState();

  const {
    entries,
    client: { modules },
  } = publicTest;
  const lastEntry = entries[entries.length - 1];
  const hasReflectionModuleEnabled = modules?.includes(Module.Reflection);
  const hasSolutionModuleEnabled = modules?.includes(Module.Solution);

  const nextModule = useMemo(() => {
    if (currentModule === Module.Analytics) {
      if (hasReflectionModuleEnabled) {
        return Module.Reflection;
      }

      if (hasSolutionModuleEnabled) {
        return Module.Solution;
      }
    }

    if (currentModule === Module.Reflection) {
      if (hasSolutionModuleEnabled) {
        return Module.Solution;
      }
    }

    return null;
  }, [currentModule, hasReflectionModuleEnabled, hasSolutionModuleEnabled]);

  const endHtml = useMemo(() => {
    if (currentModule === Module.Analytics) {
      return publicTest.endHtml;
    }

    if (currentModule === Module.Reflection) {
      return publicTest.module2EndHtml;
    }

    if (currentModule === Module.Solution) {
      return publicTest.module3EndHtml;
    }

    return "";
  }, [currentModule, publicTest]);

  async function handleDownloadPdfClick() {
    setIsLoading(true);
    try {
      await handlePdfFileDownload(
        lastEntry.sessionId,
        t("frontend.test.reportFileName", "sl-cube-report.pdf")
      );
    } catch {
      toast.error(
        t(
          "frontend.test.pdfExportError",
          "Error exporting PDF. Please try again later."
        )
      );
    }
    setIsLoading(false);
  }

  function handleReflectionModuleStart() {
    navigateToParticipationPage({
      module: Module.Reflection,
      subPage: INTRO_SUB_URL,
    });
  }

  function handleSolutionModuleStart() {
    navigateToParticipationPage({
      module: Module.Solution,
      subPage: INTRO_SUB_URL,
    });
  }

  return (
    <div
      className={classNames(
        "tg_page tg_page-layout tg_module-end",
        `module-${currentModule}`
      )}
    >
      <div className="tg_main tg_column">
        <div
          className="tg_end-html tg_info tg_test-info"
          dangerouslySetInnerHTML={{
            __html: endHtml,
          }}
        />
      </div>
      <div className="tg_module-end-actions tg_column">
        {!nextModule ? (
          <Button onClick={handleDownloadPdfClick} large disabled={isLoading}>
            {t("frontend.test.downloadPdf", "Download PDF")}
          </Button>
        ) : (
          <>
            {nextModule === Module.Reflection && (
              <Button
                onClick={handleReflectionModuleStart}
                large
                disabled={isLoading}
              >
                {t(
                  "frontend.test.startReflectionModule",
                  "Reflect Leverages & Challenges"
                )}
              </Button>
            )}
            {nextModule === Module.Solution && (
              <Button
                onClick={handleSolutionModuleStart}
                large
                disabled={isLoading}
              >
                {t(
                  "frontend.test.startSolutionModule",
                  "Solution for one Challenge"
                )}
              </Button>
            )}
          </>
        )}
        {isLoading && (
          <div className="export-loader-container">
            <Loader />
          </div>
        )}
      </div>
      <BottomBar />
    </div>
  );
}
